(function () {
  "use strict";

  const STEP = {
    DETAILS: 1,
    ITEMS: 2,
    SUBMIT: 3,
  };

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function normalizeOrderNumber(value) {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "";
    return trimmed.startsWith("*") ? trimmed : `*${trimmed}`;
  }

  function formatMoney(amount, currency) {
    const numeric = Number(amount);
    if (Number.isNaN(numeric)) return amount;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || "EUR",
      }).format(numeric);
    } catch {
      return `${numeric.toFixed(2)} ${currency || ""}`.trim();
    }
  }

  class EuWithdrawalForm extends HTMLElement {
    constructor() {
      super();
      this.currentStep = STEP.DETAILS;
      this.orderData = null;
      this.itemState = new Map();
      this.isLoading = false;
    }

    connectedCallback() {
      this.cacheElements();
      this.bindEvents();
      this.renderStepIndicator();
    }

    cacheElements() {
      this.stepEls = Array.from(
        this.querySelectorAll("[data-eu-withdrawal-step-indicator]")
      );
      this.panels = {
        [STEP.DETAILS]: this.querySelector('[data-eu-withdrawal-panel="details"]'),
        [STEP.ITEMS]: this.querySelector('[data-eu-withdrawal-panel="items"]'),
        [STEP.SUBMIT]: this.querySelector('[data-eu-withdrawal-panel="submit"]'),
      };
      this.formAlert = this.querySelector("[data-eu-withdrawal-alert]");
      this.itemsContainer = this.querySelector("[data-eu-withdrawal-items]");
      this.summaryContainer = this.querySelector("[data-eu-withdrawal-summary]");
      this.selectAllInput = this.querySelector("[data-eu-withdrawal-select-all]");
      this.declaration = this.querySelector("[data-eu-withdrawal-declaration]");
      this.declarationInput = this.querySelector(
        "[data-eu-withdrawal-declaration-input]"
      );
      this.modal = this.querySelector("[data-eu-withdrawal-modal]");
      this.modalTitle = this.querySelector("[data-eu-withdrawal-modal-title]");
      this.modalText = this.querySelector("[data-eu-withdrawal-modal-text]");
      this.modalIcon = this.querySelector("[data-eu-withdrawal-modal-icon]");
      this.modalAction = this.querySelector("[data-eu-withdrawal-modal-action]");
      this.lookupEndpoint = this.dataset.lookupEndpoint || "";
      this.submitEndpoint = this.dataset.submitEndpoint || "";
      this.contactUrl = this.dataset.contactUrl || "/pages/contact-us";
    }

    bindEvents() {
      this.querySelector("[data-eu-withdrawal-continue-details]")?.addEventListener(
        "click",
        () => this.handleContinueDetails()
      );
      this.querySelector("[data-eu-withdrawal-back-items]")?.addEventListener(
        "click",
        () => this.goToStep(STEP.DETAILS)
      );
      this.querySelector("[data-eu-withdrawal-continue-items]")?.addEventListener(
        "click",
        () => this.handleContinueItems()
      );
      this.querySelector("[data-eu-withdrawal-back-submit]")?.addEventListener(
        "click",
        () => this.goToStep(STEP.ITEMS)
      );
      this.querySelector("[data-eu-withdrawal-submit]")?.addEventListener(
        "click",
        () => this.handleSubmit()
      );

      this.querySelector("[data-eu-withdrawal-order-number]")?.addEventListener(
        "blur",
        (event) => {
          event.target.value = normalizeOrderNumber(event.target.value);
        }
      );

      this.selectAllInput?.addEventListener("change", (event) => {
        this.toggleSelectAll(event.target.checked);
      });

      this.modalAction?.addEventListener("click", () => {
        if (this.modalAction.dataset.redirect === "contact") {
          window.location.href = this.contactUrl;
          return;
        }
        this.closeModal();
      });

      this.modal?.addEventListener("click", (event) => {
        if (
          event.target === this.modal ||
          event.target.matches("[data-eu-withdrawal-modal-close]")
        ) {
          this.closeModal();
        }
      });
    }

    getDetails() {
      return {
        fullName: this.querySelector("[data-eu-withdrawal-full-name]")?.value.trim(),
        orderNumber: normalizeOrderNumber(
          this.querySelector("[data-eu-withdrawal-order-number]")?.value
        ),
        email: this.querySelector("[data-eu-withdrawal-email]")?.value.trim(),
      };
    }

    clearAlert() {
      if (!this.formAlert) return;
      this.formAlert.hidden = true;
      this.formAlert.textContent = "";
    }

    showAlert(message) {
      if (!this.formAlert) return;
      this.formAlert.hidden = false;
      this.formAlert.textContent = message;
    }

    clearFieldErrors() {
      this.querySelectorAll(".eu-withdrawal__field.is-error").forEach((field) => {
        field.classList.remove("is-error");
        const error = field.querySelector(".eu-withdrawal__field-error");
        if (error) error.remove();
      });
    }

    setFieldError(selector, message) {
      const input = this.querySelector(selector);
      if (!input) return;
      const field = input.closest(".eu-withdrawal__field");
      if (!field) return;
      field.classList.add("is-error");
      const error = document.createElement("p");
      error.className = "eu-withdrawal__field-error";
      error.textContent = message;
      field.appendChild(error);
    }

    validateDetails() {
      this.clearFieldErrors();
      this.clearAlert();
      const details = this.getDetails();
      let valid = true;

      if (!details.fullName) {
        this.setFieldError(
          "[data-eu-withdrawal-full-name]",
          "Please enter your full name."
        );
        valid = false;
      }

      if (!details.orderNumber) {
        this.setFieldError(
          "[data-eu-withdrawal-order-number]",
          "Please enter your order number."
        );
        valid = false;
      }

      if (!details.email) {
        this.setFieldError(
          "[data-eu-withdrawal-email]",
          "Please enter your email address."
        );
        valid = false;
      } else if (!EMAIL_PATTERN.test(details.email)) {
        this.setFieldError(
          "[data-eu-withdrawal-email]",
          "Please enter a valid email address."
        );
        valid = false;
      }

      return valid;
    }

    async handleContinueDetails() {
      if (this.isLoading || !this.validateDetails()) return;

      const details = this.getDetails();
      this.setButtonLoading("[data-eu-withdrawal-continue-details]", true);

      try {
        const orderData = await this.lookupOrder(details);
        this.orderData = orderData;
        this.initializeItemState(orderData.lineItems || []);
        this.renderItems();
        this.goToStep(STEP.ITEMS);
      } catch (error) {
        this.showAlert(error.message || "Unable to find this order. Please check your details and try again.");
      } finally {
        this.setButtonLoading("[data-eu-withdrawal-continue-details]", false);
      }
    }

    async lookupOrder(details) {
      if (!this.lookupEndpoint) {
        throw new Error("Withdrawal service is not configured yet.");
      }

      const response = await fetch(this.lookupEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          fullName: details.fullName,
          orderNumber: details.orderNumber,
          email: details.email,
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload.message ||
            "Unable to find this order. Please check your details and try again."
        );
      }

      return payload;
    }

    initializeItemState(lineItems) {
      this.itemState = new Map(
        lineItems.map((item) => [
          String(item.id),
          {
            ...item,
            selected: false,
            returnQty: 1,
          },
        ])
      );
    }

    renderItems() {
      if (!this.itemsContainer) return;
      this.itemsContainer.innerHTML = "";

      const items = Array.from(this.itemState.values());
      if (!items.length) {
        this.itemsContainer.innerHTML =
          '<p class="eu-withdrawal__panel-desc">No items were found for this order.</p>';
        return;
      }

      items.forEach((item) => {
        this.itemsContainer.appendChild(this.createItemRow(item));
      });

      this.syncSelectAllCheckbox();
    }

    createItemRow(item) {
      const row = document.createElement("article");
      row.className = "eu-withdrawal__item";
      row.dataset.lineItemId = item.id;

      const imageMarkup = item.image
        ? `<div class="eu-withdrawal__item-media"><img class="eu-withdrawal__item-image" src="${item.image}" alt="" loading="lazy" width="80" height="80"></div>`
        : `<div class="eu-withdrawal__item-media"><span class="eu-withdrawal__item-image eu-withdrawal__item-image--placeholder" aria-hidden="true"></span></div>`;

      row.innerHTML = `
        <div class="eu-withdrawal__item-check">
          <input type="checkbox" data-eu-withdrawal-item-check aria-label="Select ${item.title}">
        </div>
        ${imageMarkup}
        <div class="eu-withdrawal__item-info">
          <p class="eu-withdrawal__item-title"></p>
          <p class="eu-withdrawal__item-meta"></p>
        </div>
        <div class="eu-withdrawal__qty" data-eu-withdrawal-qty>
          <button type="button" class="eu-withdrawal__qty-btn" data-eu-withdrawal-qty-dec aria-label="Decrease quantity">−</button>
          <span class="eu-withdrawal__qty-value" data-eu-withdrawal-qty-value>1</span>
          <button type="button" class="eu-withdrawal__qty-btn" data-eu-withdrawal-qty-inc aria-label="Increase quantity">+</button>
        </div>
      `;

      row.querySelector(".eu-withdrawal__item-title").textContent = item.title;
      row.querySelector(".eu-withdrawal__item-meta").textContent = formatMoney(
        item.price,
        item.currency
      );

      const checkbox = row.querySelector("[data-eu-withdrawal-item-check]");
      const decBtn = row.querySelector("[data-eu-withdrawal-qty-dec]");
      const incBtn = row.querySelector("[data-eu-withdrawal-qty-inc]");
      const qtyValue = row.querySelector("[data-eu-withdrawal-qty-value]");

      const syncRow = () => {
        const state = this.itemState.get(String(item.id));
        checkbox.checked = state.selected;
        qtyValue.textContent = String(state.returnQty);
        incBtn.disabled = state.returnQty >= state.quantity;
        decBtn.disabled = state.returnQty <= 1;
      };

      checkbox.addEventListener("change", () => {
        const state = this.itemState.get(String(item.id));
        state.selected = checkbox.checked;
        this.syncSelectAllCheckbox();
      });

      decBtn.addEventListener("click", () => {
        const state = this.itemState.get(String(item.id));
        if (state.returnQty > 1) {
          state.returnQty -= 1;
          syncRow();
        }
      });

      incBtn.addEventListener("click", () => {
        const state = this.itemState.get(String(item.id));
        if (state.returnQty < state.quantity) {
          state.returnQty += 1;
          syncRow();
        }
      });

      syncRow();
      return row;
    }

    toggleSelectAll(checked) {
      this.itemState.forEach((state) => {
        state.selected = checked;
      });
      this.querySelectorAll("[data-eu-withdrawal-item-check]").forEach((input) => {
        input.checked = checked;
      });
    }

    syncSelectAllCheckbox() {
      if (!this.selectAllInput) return;
      const items = Array.from(this.itemState.values());
      const selectedCount = items.filter((item) => item.selected).length;
      this.selectAllInput.checked =
        items.length > 0 && selectedCount === items.length;
      this.selectAllInput.indeterminate =
        selectedCount > 0 && selectedCount < items.length;
    }

    getSelectedItems() {
      return Array.from(this.itemState.values()).filter((item) => item.selected);
    }

    isFullOrderWithdrawal() {
      return this.getSelectedItems().length === 0;
    }

    getReason() {
      const value = this.querySelector("[data-eu-withdrawal-reason]")?.value;
      return value && value.trim() ? value.trim() : "";
    }

    handleContinueItems() {
      this.clearAlert();
      this.renderSummary();
      this.goToStep(STEP.SUBMIT);
    }

    renderSummary() {
      if (!this.summaryContainer) return;
      const details = this.getDetails();
      const selectedItems = this.getSelectedItems();
      const fullOrder = this.isFullOrderWithdrawal();

      let itemsMarkup = "";
      if (fullOrder) {
        itemsMarkup = `
          <div class="eu-withdrawal__summary-line">
            <span class="eu-withdrawal__summary-line-name">[Full Order Withdrawal]</span>
            <span class="eu-withdrawal__summary-line-qty" aria-hidden="true"></span>
            <span class="eu-withdrawal__summary-line-price" aria-hidden="true"></span>
          </div>
        `;
      } else {
        itemsMarkup = selectedItems
          .map(
            (item) => `
              <div class="eu-withdrawal__summary-line">
                <span class="eu-withdrawal__summary-line-name">${item.title}</span>
                <span class="eu-withdrawal__summary-line-qty">x${item.returnQty}</span>
                <span class="eu-withdrawal__summary-line-price">${formatMoney(item.price, item.currency)}</span>
              </div>
            `
          )
          .join("");
      }

      this.summaryContainer.innerHTML = `
        <h3 class="eu-withdrawal__summary-title">Withdrawal Summary</h3>
        <div class="eu-withdrawal__summary-meta">
          <div class="eu-withdrawal__summary-meta-item">
            <span class="eu-withdrawal__summary-meta-label">Order Number</span>
            <span class="eu-withdrawal__summary-meta-value">${details.orderNumber}</span>
          </div>
          <span class="eu-withdrawal__summary-meta-divider" aria-hidden="true"></span>
          <div class="eu-withdrawal__summary-meta-item">
            <span class="eu-withdrawal__summary-meta-label">Email Address</span>
            <span class="eu-withdrawal__summary-meta-value">${details.email}</span>
          </div>
        </div>
        <h4 class="eu-withdrawal__summary-items-title">Selected Items</h4>
        <div class="eu-withdrawal__summary-list">
          ${itemsMarkup}
        </div>
      `;
    }

    validateSubmit() {
      this.declaration?.classList.remove("is-error");
      if (this.declarationInput?.checked) return true;
      this.declaration?.classList.add("is-error");
      this.showAlert("Please check this box to proceed.");
      return false;
    }

    buildSubmissionPayload() {
      const details = this.getDetails();
      const fullOrder = this.isFullOrderWithdrawal();
      const selectedItems = this.getSelectedItems();

      return {
        fullName: details.fullName,
        orderNumber: details.orderNumber,
        email: details.email,
        orderId: this.orderData?.orderId || null,
        fullOrderWithdrawal: fullOrder,
        reason: this.getReason() || "Not specified",
        items: fullOrder
          ? []
          : selectedItems.map((item) => ({
              id: item.id,
              variantId: item.variantId || null,
              sku: item.sku || null,
              title: item.title,
              quantity: item.returnQty,
            })),
      };
    }

    async handleSubmit() {
      if (this.isLoading) return;
      this.clearAlert();
      if (!this.validateSubmit()) return;

      this.setButtonLoading("[data-eu-withdrawal-submit]", true);

      try {
        const result = await this.submitWithdrawal(this.buildSubmissionPayload());
        this.showResultModal("success", result);
      } catch (error) {
        if (error.code === "expired") {
          this.showResultModal("expired", error);
        } else if (error.code === "duplicate") {
          this.showResultModal("duplicate", error);
        } else {
          this.showAlert(error.message || "Unable to submit your request. Please try again.");
        }
      } finally {
        this.setButtonLoading("[data-eu-withdrawal-submit]", false);
      }
    }

    async submitWithdrawal(payload) {
      if (!this.submitEndpoint) {
        throw new Error("Withdrawal service is not configured yet.");
      }

      const response = await fetch(this.submitEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success !== false) {
        return data;
      }

      const error = new Error(
        data.message || "Unable to submit your request. Please try again."
      );
      error.code = data.code || "unknown";
      throw error;
    }

    showResultModal(type, payload = {}) {
      const configs = {
        success: {
          iconClass: "eu-withdrawal-modal__icon--success",
          icon: "✓",
          title: "Request Submitted Successfully",
          text:
            payload.message ||
            "Your withdrawal request has been received. Our customer service team will review your order details and get back to you with return instructions within 48 hours. Thank you for your patience.",
          actionText: "Close",
          redirect: "",
        },
        expired: {
          iconClass: "eu-withdrawal-modal__icon--error",
          icon: "!",
          title: "Submission Unavailable",
          text:
            payload.message ||
            "We are sorry, but this order has exceeded the 14-day statutory EU withdrawal period (calculated from the date of delivery). If you require standard warranty or regular after-sales support, please contact our Support Team.",
          actionText: "Go to Support Center",
          redirect: "contact",
        },
        duplicate: {
          iconClass: "eu-withdrawal-modal__icon--error",
          icon: "!",
          title: "Submission Unavailable",
          text:
            payload.message ||
            "You can only submit one withdrawal request per order. If you need to make changes to an existing withdrawal request, please contact our Support Team.",
          actionText: "Go to Support Center",
          redirect: "contact",
        },
      };

      const config = configs[type] || configs.success;
      this.modalIcon.className = `eu-withdrawal-modal__icon ${config.iconClass}`;
      this.modalIcon.textContent = config.icon;
      this.modalTitle.textContent = config.title;
      this.modalText.textContent = config.text;
      this.modalAction.textContent = config.actionText;
      this.modalAction.dataset.redirect = config.redirect;
      if (typeof this.modal.open === "function") {
        this.modal.open(this.modalAction);
      } else {
        this.modal.setAttribute("open", "");
      }
    }

    closeModal() {
      if (typeof this.modal.close === "function") {
        this.modal.close();
      } else {
        this.modal?.removeAttribute("open");
      }
    }

    goToStep(step) {
      this.currentStep = step;
      this.clearAlert();
      Object.entries(this.panels).forEach(([stepKey, panel]) => {
        if (!panel) return;
        panel.hidden = Number(stepKey) !== step;
      });
      this.renderStepIndicator();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    renderStepIndicator() {
      const stepsRoot = this.querySelector(".eu-withdrawal__steps");
      if (stepsRoot) {
        stepsRoot.dataset.progress = String(this.currentStep);
      }

      this.stepEls.forEach((stepEl) => {
        const stepNumber = Number(stepEl.dataset.euWithdrawalStepIndicator);
        stepEl.classList.toggle("is-active", stepNumber === this.currentStep);
        stepEl.classList.toggle("is-complete", stepNumber < this.currentStep);
      });
    }

    setButtonLoading(selector, loading) {
      const button = this.querySelector(selector);
      if (!button) return;
      this.isLoading = loading;
      button.disabled = loading;
      const label = button.querySelector("[data-eu-withdrawal-btn-label]");
      const spinner = button.querySelector("[data-eu-withdrawal-btn-spinner]");
      if (label) label.hidden = loading;
      if (spinner) spinner.hidden = !loading;
    }
  }

  if (!customElements.get("eu-withdrawal-form")) {
    customElements.define("eu-withdrawal-form", EuWithdrawalForm);
  }
})();
